import { useState, useEffect } from 'react';
import { fetchBillingSettings, updateBillingSettings } from '../services/api';
import './Settings.css';

const TEMPLATE_VARIABLES = [
  { token: '{{client_name}}',    desc: 'Client business name' },
  { token: '{{facility_name}}',  desc: 'Site / facility name' },
  { token: '{{invoice_number}}', desc: 'Invoice number (e.g. WC-2026-05)' },
  { token: '{{invoice_date}}',   desc: 'Invoice date' },
  { token: '{{due_date}}',       desc: 'Payment due date' },
  { token: '{{period_start}}',   desc: 'Service period start date' },
  { token: '{{period_end}}',     desc: 'Service period end date' },
  { token: '{{total}}',          desc: 'Invoice total amount' },
  { token: '{{balance_due}}',    desc: 'Balance currently due' },
  { token: '{{biller_name}}',    desc: 'Biller contact name' },
  { token: '{{biller_phone}}',   desc: 'Biller contact phone' },
  { token: '{{biller_email}}',   desc: 'Biller contact email' },
];

function BillingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    biller_name: '', biller_phone: '', biller_email: '', biller_address: '',
    default_terms: '', default_email_subject: '', default_email_body: '',
    // ⟳ Step 14 (S14-j): blank = the 60-day default.
    patient_credit_due_days: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchBillingSettings();
        const d = res.data || {};
        setForm({
          biller_name: d.biller_name || '',
          biller_phone: d.biller_phone || '',
          biller_email: d.biller_email || '',
          biller_address: d.biller_address || '',
          default_terms: d.default_terms || '',
          default_email_subject: d.default_email_subject || '',
          default_email_body: d.default_email_body || '',
          patient_credit_due_days: d.patient_credit_due_days == null ? '' : String(d.patient_credit_due_days),
        });
      } catch (err) {
        console.error('Failed to load billing settings:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.biller_name.trim()) {
      setMsg({ type: 'error', text: 'Biller name is required.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await updateBillingSettings(form);
      setMsg({ type: 'success', text: 'Billing settings saved.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Billing Settings</h1>
        <p>Global biller contact info and defaults applied to every client invoice</p>
      </div>

      {msg && (
        <div className={`settings-alert ${msg.type}`} style={{ marginBottom: 20 }}>
          {msg.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Biller Contact */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon profile">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="settings-card-header-text">
              <h2>Biller Contact</h2>
              <p>Printed at the bottom of every invoice as the questions-about-this-invoice contact</p>
            </div>
          </div>

          <div className="settings-form">
            <div className="settings-form-row">
              <div className="settings-field">
                <label>Biller Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  className="settings-input"
                  placeholder="e.g. Caden Dye"
                  value={form.biller_name}
                  onChange={e => setField('biller_name', e.target.value)}
                />
              </div>
              <div className="settings-field">
                <label>Phone</label>
                <input
                  type="text"
                  className="settings-input"
                  placeholder="(555) 000-0000"
                  value={form.biller_phone}
                  onChange={e => setField('biller_phone', e.target.value)}
                />
              </div>
            </div>

            <div className="settings-form-row">
              <div className="settings-field">
                <label>Email</label>
                <input
                  type="email"
                  className="settings-input"
                  placeholder="billing@yourcompany.com"
                  value={form.biller_email}
                  onChange={e => setField('biller_email', e.target.value)}
                />
              </div>
              <div className="settings-field">
                <label>Address</label>
                <textarea
                  className="settings-input"
                  rows={2}
                  placeholder="Street, City, State Zip"
                  value={form.biller_address}
                  onChange={e => setField('biller_address', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ⟳ Step 14 (S14-j) — patient refunds */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-header-text">
              <h2>Patient Credit Refunds</h2>
              <p>
                How many days after a patient overpayment is identified the refund is due. Missouri sets no
                deadline, so this is office policy; leave it blank for 60 days. Payer overpayments stay on the
                federal 60-day clock regardless.
              </p>
            </div>
          </div>
          <div className="settings-form">
            <div className="settings-field">
              <label htmlFor="patient-credit-due-days">Refund due within (days)</label>
              <input
                id="patient-credit-due-days"
                className="settings-input"
                type="number"
                min="1"
                max="3650"
                step="1"
                placeholder="60"
                value={form.patient_credit_due_days}
                onChange={e => setField('patient_credit_due_days', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Default Terms */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon security">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="settings-card-header-text">
              <h2>Default Payment Terms</h2>
              <p>Printed on every invoice. Per-client payment terms (Net 30, Net 45) still drive the due date.</p>
            </div>
          </div>

          <div className="settings-form">
            <div className="settings-field">
              <label>Terms Text</label>
              <textarea
                className="settings-input"
                rows={3}
                placeholder="Payment due within 30 days..."
                value={form.default_terms}
                onChange={e => setField('default_terms', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Default Email Template */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon profile">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="settings-card-header-text">
              <h2>Invoice Email Template</h2>
              <p>Pre-fills the Send Invoice modal. Editable per send.</p>
            </div>
          </div>

          <div className="settings-form">
            <div className="settings-field">
              <label>Subject</label>
              <input
                type="text"
                className="settings-input"
                value={form.default_email_subject}
                onChange={e => setField('default_email_subject', e.target.value)}
              />
            </div>
            <div className="settings-field">
              <label>Body</label>
              <textarea
                className="settings-input"
                rows={10}
                style={{ fontFamily: 'inherit', resize: 'vertical' }}
                value={form.default_email_body}
                onChange={e => setField('default_email_body', e.target.value)}
              />
              <p className="settings-field-hint" style={{ marginTop: 12, marginBottom: 8 }}>
                Available template variables (click to copy):
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {TEMPLATE_VARIABLES.map(v => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(v.token)}
                    title={v.desc}
                    style={{
                      background: '#eef2ff', border: '1px solid #c7d2fe', color: '#171363',
                      fontSize: '0.72rem', fontFamily: 'monospace', padding: '3px 8px',
                      borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    {v.token}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="settings-actions" style={{ marginTop: 8 }}>
          <button type="submit" className="settings-btn-primary" disabled={saving}>
            {saving && <span className="settings-spinner" />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BillingSettings;
